import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Activity, ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ToolCard from '@/components/ToolCard';
import SponsorSection from '@/components/SponsorSection';
import CategoryFilter from '@/components/CategoryFilter';
import PlatformFilter from '@/components/PlatformFilter';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Tool } from '@/data/tools';
import MetaTags from '@/components/MetaTags';
import ShareButton from '@/components/ShareButton';

const SportPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [allTools, setAllTools] = useState<Tool[]>([]);
  const [sport, setSport] = useState<string>('');
  const [sportSeoDescription, setSportSeoDescription] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [sponsors, setSponsors] = useState<{
    gold: Tool | null;
    silver: Tool | null;
    bronze: Tool | null;
  }>({ gold: null, silver: null, bronze: null });
  
  // Add states for filters
  const [categories, setCategories] = useState<{ id: string, name: string }[]>([]);
  const [platforms, setPlatforms] = useState<{ id: string, name: string }[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null);

  // Set meta tags for social sharing
  const pageTitle = sport ? `${sport} Apps - FitnessAppSearch` : 'Loading...';
  const pageDescription = sportSeoDescription || (sport ? `Discover the best apps and tools for ${sport.toLowerCase()}.` : '');
  const canonicalUrl = `${window.location.origin}/sport/${slug}`;
  const imageUrl = `${window.location.origin}/og-image.png`;

  useEffect(() => {
    const fetchSportData = async () => {
      setLoading(true);
      
      try {
        // First, get the sport details and sponsor IDs
        const { data: sportData, error: sportError } = await supabase
          .from('sports')
          .select(`
            id, 
            name,
            page_title,
            seo_description,
            gold_sponsor_app_id,
            silver_sponsor_app_id,
            bronze_sponsor_app_id
          `)
          .eq('url_slug', slug)
          .single();
        
        if (sportError) throw sportError;
        
        if (sportData) {
          setSport(sportData.name);
          setSportSeoDescription(sportData.seo_description || '');
          
          // Fetch sponsor apps if they exist
          const sponsorIds = [
            sportData.gold_sponsor_app_id,
            sportData.silver_sponsor_app_id,
            sportData.bronze_sponsor_app_id
          ].filter(Boolean);

          if (sponsorIds.length > 0) {
            const { data: sponsorApps, error: sponsorError } = await supabase
              .from('apps')
              .select(`
                id,
                name,
                description,
                visit_url,
                url_slug,
                approved,
                featured,
                logo_url,
                app_platforms(platform_id, platforms(name)),
                app_categories(category_id, categories(name))
              `)
              .in('id', sponsorIds)
              .eq('approved', true);

            if (sponsorError) throw sponsorError;

            if (sponsorApps) {
              const transformedSponsors = sponsorApps.map(app => ({
                id: app.id,
                name: app.name,
                logo: app.logo_url || "https://placehold.co/60x60",
                rating: 4.5,
                reviews: 0,
                bookmarks: 0,
                pricing: "Free Trial",
                description: app.description,
                tags: [],
                category: app.app_categories[0]?.categories?.name || "",
                sports: [sportData.name],
                platforms: Array.from(new Set(app.app_platforms.map((p: any) => p.platforms?.name).filter(Boolean))),
                featured: app.featured,
                visitUrl: app.visit_url,
                url_slug: app.url_slug,
              }));

              setSponsors({
                gold: transformedSponsors.find(s => s.id === sportData.gold_sponsor_app_id) || null,
                silver: transformedSponsors.find(s => s.id === sportData.silver_sponsor_app_id) || null,
                bronze: transformedSponsors.find(s => s.id === sportData.bronze_sponsor_app_id) || null,
              });
            }
          }

          // Then get all regular apps for this sport
          const { data: appsData, error: appsError } = await supabase
            .from('apps')
            .select(`
              id,
              name,
              description,
              visit_url,
              url_slug,
              approved,
              featured,
              logo_url,
              app_sports!inner(sport_id),
              app_platforms(platform_id, platforms(name)),
              app_categories(category_id, categories(name))
            `)
            .eq('approved', true)
            .eq('app_sports.sport_id', sportData.id);
          
          if (appsError) throw appsError;
          
          if (appsData) {
            // Transform the data into the Tool format
            const transformedTools: Tool[] = appsData.map(app => {
              // Extract unique categories and platforms
              const categories = Array.from(new Set(
                app.app_categories.map((c: any) => c.categories?.name).filter(Boolean)
              ));
              
              const platforms = Array.from(new Set(
                app.app_platforms.map((p: any) => p.platforms?.name).filter(Boolean)
              ));
              
              return {
                id: app.id,
                name: app.name,
                logo: app.logo_url || "https://placehold.co/60x60",
                rating: 4.5, // Default rating
                reviews: 0, // Default reviews count
                bookmarks: 0, // Default bookmarks count
                pricing: "Free Trial", // Default pricing
                description: app.description,
                tags: [], // Could be derived from categories
                category: categories[0] || "",
                sports: [sportData.name], // We know it has this sport
                platforms: platforms,
                featured: app.featured,
                visitUrl: app.visit_url,
                url_slug: app.url_slug,
              };
            });
            
            setAllTools(transformedTools);
            setFilteredTools(transformedTools);
          }
          
          // Fetch all categories
          const { data: categoriesData, error: categoriesError } = await supabase
            .from('categories')
            .select('id, name')
            .order('name');
            
          if (categoriesError) throw categoriesError;
          if (categoriesData) {
            setCategories(categoriesData);
          }
          
          // Fetch all platforms
          const { data: platformsData, error: platformsError } = await supabase
            .from('platforms')
            .select('id, name')
            .order('name');
            
          if (platformsError) throw platformsError;
          if (platformsData) {
            setPlatforms(platformsData);
          }
        }
      } catch (error) {
        console.error('Error fetching sport data:', error);
        toast({
          title: "Error loading data",
          description: "There was a problem loading the sport data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchSportData();
    }
  }, [slug, toast]);
  
  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryIds(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };
  
  // Handle platform selection
  const handlePlatformSelect = (platformId: string | null) => {
    setSelectedPlatformId(platformId);
  };
  
  // Apply filters effect
  useEffect(() => {
    if (allTools.length === 0) return;
    
    let filtered = [...allTools];
    
    // Filter by categories if any selected
    if (selectedCategoryIds.length > 0) {
      filtered = filtered.filter(tool => {
        const toolCategories = allTools.find(t => t.id === tool.id)?.category;
        if (!toolCategories) return false;
        
        // Find the category object that matches the tool's category name
        const categoryObj = categories.find(c => c.name === toolCategories);
        if (!categoryObj) return false;
        
        return selectedCategoryIds.includes(categoryObj.id);
      });
    }
    
    // Filter by platform if selected
    if (selectedPlatformId) {
      const platformName = platforms.find(p => p.id === selectedPlatformId)?.name;
      filtered = filtered.filter(tool => 
        tool.platforms.some(p => p === platformName)
      );
    }
    
    setFilteredTools(filtered);
  }, [allTools, selectedCategoryIds, selectedPlatformId, categories, platforms]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MetaTags
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        imageUrl={imageUrl}
      />
      
      <Navigation />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <Link to="/sports" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Sports
        </Link>
        
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-gray-600" />
            <h1 className="text-3xl font-bold">{sport} Apps</h1>
          </div>
          <ShareButton title={`${sport} Apps and Tools`} />
        </div>
        
        <p className="text-gray-600 mb-8">
          Discover the best apps and tools for {sport.toLowerCase()}.
        </p>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar with filters */}
            <div className="space-y-6">
              <CategoryFilter 
                categories={categories} 
                selectedCategoryIds={selectedCategoryIds} 
                onSelectCategory={handleCategorySelect} 
              />
              <PlatformFilter 
                platforms={platforms} 
                selectedPlatformId={selectedPlatformId} 
                onSelectPlatform={handlePlatformSelect} 
              />
            </div>
            
            {/* Main content */}
            <div className="lg:col-span-3">
              <SponsorSection
                goldSponsor={sponsors.gold}
                silverSponsor={sponsors.silver}
                bronzeSponsor={sponsors.bronze}
              />
              
              {filteredTools.length > 0 ? (
                <>
                  <div className="mb-6 border-b pb-2">
                    <h2 className="text-2xl font-semibold">All {sport} Tools</h2>
                  </div>
                  <div className="grid gap-4">
                    {filteredTools.map(tool => (
                      <ToolCard key={tool.id} tool={tool} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <h2 className="text-xl font-medium text-gray-900 mb-2">No apps found</h2>
                  <p className="text-gray-600">
                    We couldn't find any apps for {sport.toLowerCase()} with the selected filters. Try changing your filters.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default SportPage;
